import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { Invoice } from './InvoiceDoc';
import { Modal, TextInput, useToast } from './ui';

/** Shape only: the server checks the address properly, this just keeps typos from being sent. */
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

interface EmailResult {
  sent: number;
  results: Array<{ kind: 'customer' | 'custom'; to: string; sent: boolean }>;
}

/**
 * Sends an invoice by email.
 *
 * Two ways to pick who gets it. The customer's address on record is open to
 * anybody who can see the invoice; it is shown masked, because a person
 * confirming "yes, that is them" does not need the whole address and a screen
 * left open at a counter should not hold it. The second box - any address at
 * all - is the owner's alone, and is not drawn for anybody else. That is
 * convenience, not protection: the server refuses the address from any other
 * role whatever this page does.
 */
export function EmailInvoiceModal({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const { can } = useAuth();
  const { push, fail } = useToast();
  const hint = invoice.customerEmailHint ?? null;
  const mayChoose = can('invoice:email-custom');

  const [toCustomer, setToCustomer] = useState(hint !== null);
  const [custom, setCustom] = useState('');

  const customEmail = custom.trim();
  const customInvalid = customEmail !== '' && !LOOKS_LIKE_EMAIL.test(customEmail);
  const nothingToSend = !(toCustomer && hint) && customEmail === '';

  const send = useMutation({
    mutationFn: () =>
      api.post<EmailResult>(`/api/invoices/${invoice.id}/email`, {
        toCustomer: toCustomer && hint !== null,
        ...(mayChoose && customEmail ? { customEmail } : {}),
      }),
    onSuccess: (result) => {
      const where = result.results.filter((r) => r.sent).map((r) => r.to);
      push(`Invoice ${invoice.number} emailed to ${where.join(' and ')}`, 'ok');
      // One address may have gone while the other did not; say so rather than
      // letting a success toast imply both.
      const missed = result.results.filter((r) => !r.sent).map((r) => r.to);
      if (missed.length > 0) fail(new Error(`Could not send to ${missed.join(' and ')}`));
      onClose();
    },
    onError: fail,
  });

  return (
    <Modal
      title={`Email invoice ${invoice.number}`}
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={send.isPending}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={nothingToSend || customInvalid || send.isPending}
            onClick={() => send.mutate()}
          >
            {send.isPending ? 'Sending…' : 'Send invoice'}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <p className="dim">
          The tax invoice is sent in the body of the email, ready to read and print.
        </p>

        <label className="checkbox" style={hint ? undefined : { opacity: 0.6 }}>
          <input
            type="checkbox"
            checked={toCustomer && hint !== null}
            disabled={hint === null}
            onChange={(e) => setToCustomer(e.target.checked)}
          />
          <span>
            <b>Send to the customer</b>
            <span className="small muted" style={{ display: 'block' }}>
              {hint ?? 'There is no email address on record for this customer.'}
            </span>
          </span>
        </label>

        {mayChoose ? (
          <TextInput
            label="Send to another email address"
            type="email"
            inputMode="email"
            autoComplete="off"
            placeholder="accounts@company.in"
            value={custom}
            error={customInvalid ? 'Enter a valid email address' : undefined}
            hint="Only the owner can send an invoice to an address of their choice. Each send is recorded."
            onChange={(e) => setCustom(e.target.value)}
          />
        ) : hint === null ? (
          <p className="small muted">
            With no address on record, ask the owner to send this invoice to an address of their
            choice.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
